import React, { useCallback, useState, useEffect } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import {
  TextField,
  CircularProgress,
  Box,
  Button,
  Stack,
} from '@mui/material';
import { PVHoverNavy } from '../colors';
import { useGoogleMaps } from '../providers/GoogleApiProvider';

export type Address = {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  formattedAddress?: string;
  placeId?: string;
};

interface GoogleAddressFieldProps {
  value: Address;
  onChange: (address: Address) => void;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  countries?: string[];
}

export const GoogleAddressField: React.FC<GoogleAddressFieldProps> = ({
  value,
  onChange,
  disabled = false,
  label = 'Address',
  error = false,
  helperText = '',
  required = false,
  countries = [],
}) => {
  const { isLoaded } = useGoogleMaps();
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);

  // Add effect to inject styles for the Google Places Autocomplete dropdown
  useEffect(() => {
    // Create style element
    const style = document.createElement('style');
    style.innerHTML = `
      .pac-container {
        z-index: 1400 !important; /* Higher than MUI Drawer's default z-index of 1300 */
      }
      .pac-item {
        padding: 8px 16px;
        font-size: 14px;
      }
      .pac-item-query {
        font-size: 16px;
      }
      .pac-item:hover {
        background-color: ${PVHoverNavy};
      }
    `;
    document.head.appendChild(style);

    // Cleanup
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const onLoad = useCallback(
    (autocompleteInstance: google.maps.places.Autocomplete) => {
      setAutocomplete(autocompleteInstance);
    },
    []
  );

  const onPlaceChanged = useCallback(() => {
    if (!autocomplete) return;

    setIsLoading(true);
    const place = autocomplete.getPlace();

    if (!place.address_components) {
      setIsLoading(false);
      return;
    }

    // Initialize address components
    const addressComponents: { [key: string]: string } = {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    };

    // Parse address components
    place.address_components.forEach(({ types, long_name, short_name }) => {
      if (types.includes('street_number')) {
        addressComponents.streetAddress1 = long_name;
      } else if (types.includes('route')) {
        addressComponents.streetAddress1 =
          `${addressComponents.streetAddress1} ${long_name}`.trim();
      } else if (types.includes('subpremise')) {
        addressComponents.streetAddress2 = long_name;
      } else if (types.includes('sublocality_level_1')) {
        addressComponents.city = long_name;
      } else if (types.includes('locality')) {
        addressComponents.city = long_name;
      } else if (types.includes('administrative_area_level_1')) {
        addressComponents.state = short_name;
      } else if (types.includes('postal_code')) {
        addressComponents.zip = long_name;
      } else if (types.includes('country')) {
        addressComponents.country = short_name;
      }
    });

    // Trim any extra spaces
    Object.keys(addressComponents).forEach((key) => {
      if (typeof addressComponents[key] === 'string') {
        addressComponents[key] = addressComponents[key].trim();
      }
    });

    const newAddress: Address = {
      streetAddress1: addressComponents.streetAddress1 || '',
      streetAddress2: addressComponents.streetAddress2 || '',
      city: addressComponents.city || '',
      state: addressComponents.state || '',
      zip: addressComponents.zip || '',
      country: addressComponents.country || '',
      formattedAddress: place.formatted_address || '',
      placeId: place.place_id || '',
    };

    onChange(newAddress);
    setIsLoading(false);
  }, [autocomplete, onChange]);

  const updateManualField = useCallback(
    (field: keyof Address, fieldValue: string) => {
      onChange({
        ...value,
        [field]: fieldValue,
        formattedAddress: undefined,
        placeId: undefined,
      });
    },
    [value, onChange]
  );

  if (manualEntry) {
    return (
      <Box>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Street address"
            value={value.streetAddress1}
            onChange={(e) => updateManualField('streetAddress1', e.target.value)}
            disabled={disabled}
            error={error}
            helperText={helperText}
            required={required}
            placeholder="e.g. 123 Main St or PO Box 123"
          />
          <TextField
            fullWidth
            label="Apartment, suite, etc. (optional)"
            value={value.streetAddress2}
            onChange={(e) => updateManualField('streetAddress2', e.target.value)}
            disabled={disabled}
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              fullWidth
              sx={{ flex: '1 1 200px' }}
              label="City"
              value={value.city}
              onChange={(e) => updateManualField('city', e.target.value)}
              disabled={disabled}
            />
            <TextField
              sx={{ flex: '0 0 80px' }}
              label="State"
              value={value.state}
              onChange={(e) => updateManualField('state', e.target.value)}
              disabled={disabled}
            />
            <TextField
              sx={{ flex: '0 0 120px' }}
              label="ZIP"
              value={value.zip}
              onChange={(e) => updateManualField('zip', e.target.value)}
              disabled={disabled}
            />
          </Box>
          <TextField
            fullWidth
            label="Country"
            value={value.country}
            onChange={(e) => updateManualField('country', e.target.value)}
            disabled={disabled}
            placeholder="e.g. US"
          />
        </Stack>
        {!disabled && (
          <Box sx={{ mt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setManualEntry(false)}
            >
              Use address search
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // Construct display address from parts if no formattedAddress exists
  const getDisplayAddress = () => {
    if (value.formattedAddress) {
      return value.formattedAddress;
    }

    // Build formatted address from individual components for manually-entered addresses
    const parts = [
      value.streetAddress1,
      value.streetAddress2,
      value.city,
      value.state && value.zip ? `${value.state} ${value.zip}` : value.state || value.zip,
      value.country,
    ].filter(Boolean);

    return parts.join(', ');
  };

  const searchField = (
    <TextField
      fullWidth
      label={label}
      value={getDisplayAddress()}
      onChange={(e) => {
        const userInput = e.target.value;
        if (!userInput.trim()) {
          onChange({
            streetAddress1: '',
            streetAddress2: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            formattedAddress: '',
            placeId: undefined,
          });
          return;
        }
        onChange({
          ...value,
          formattedAddress: userInput,
        });
      }}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      InputProps={{
        endAdornment: isLoading ? <CircularProgress size={20} /> : null,
      }}
    />
  );

  return (
    <Box position="relative">
      {/* Suggestions need the Maps script; until it loads (or if it never
          does) the field still accepts a typed address. */}
      {isLoaded ? (
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          restrictions={{ country: countries }} // Empty array means no country restrictions
        >
          {searchField}
        </Autocomplete>
      ) : (
        searchField
      )}
      {!disabled && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => setManualEntry(true)}
          >
            Enter address manually
          </Button>
        </Box>
      )}
    </Box>
  );
};
